import { gql } from '@apollo/client'
import { useQuery, useAnonymousQuery } from './apolloWithAuth'
import CREATIVE_REPONSES_QUERY from './queries/creativeResponses'
import PUBLIC_CREATIVE_REPONSES_QUERY from './queries/publicCreativeResponses'
import GIFT_FOR_CHAT_USER_QUERY from './queries/giftForChatUser'

type ResponsesQueryWhere = {
  sharing?: {
    _contains: 'share-on-website',
  },
  approved_at?: {
    _is_null: false,
  },
  activity_slug?: { _eq: string },
  country?: { _eq: string },
  age?: { _eq: string },
  image_object?: {
    _is_null: boolean,
  },
  audio_object?: {
    _is_null: boolean,
  },
  writing?: '' | { _neq: '' },
}

type PublicResponsesQueryWhere = ResponsesQueryWhere & {
  sharing: {
    _contains: 'share-on-website',
  },
  approved_at: {
    _is_null: false,
  },
}

export const useCreativeResponsesQuery = ( { userId = null, approved = null } ) => {
  const where = {} as any

  if ( userId ) {
    where.user_id = { _eq: userId }
  }

  if ( approved === true ) {
    where.approved_at = { _is_null: false }
  }
  
  if ( approved === false ) {
    where.approved_at = { _is_null: true }
  }

  const result = useQuery( CREATIVE_REPONSES_QUERY, {
    variables: {
      where,
    },
  } )

  const creativeResponses = result?.data?.creativeResponses || []
  return { creativeResponses, ...result }
}

export const usePublicCreativeResponsesQuery = ( { 
  limit = 30, 
  offset = null,
  activity = null,
  country = null,
  age = null,
  artform = null,
} ) => {
  const where: PublicResponsesQueryWhere = { 
    sharing: {
      _contains: 'share-on-website',
    },
    approved_at: {
      _is_null: false,
    },
  }

  if ( activity ) {
    where.activity_slug = { _eq: activity }
  }
  
  if ( country ) {
    where.country = { _eq: country }
  }
  
  if ( age ) {
    where.age = { _eq: age }
  }
  
  if ( artform === 'write' ) {
    where.writing = { _neq: '' }
  }
  else if ( artform === 'image' ) {
    where.image_object = { _is_null: false }
  }
  else if ( artform === 'sound' ) {
    where.audio_object = { _is_null: false }
  }

  const result = useAnonymousQuery( PUBLIC_CREATIVE_REPONSES_QUERY, {
    variables: {
      limit,
      offset,
      where,
    },
  } )

  const creativeResponses = result?.data?.creativeResponses || []
  return { creativeResponses, ...result }
}

export const useGiftForChatUserQuery = ( { language = null, avoidType = null, skip = false } ) => {
  const result = useAnonymousQuery( GIFT_FOR_CHAT_USER_QUERY, {
    variables: {
      avoidType,
      language,
    },
    skip,
  } )

  const creativeResponse = result?.data?.creativeResponses[ 0 ] || null
  return { creativeResponse, ...result }
}

export const useResponsesFiltersQuery = locale => {
  const activitiesResult = useAnonymousQuery( gql`
    query ResponseFiltersQuery(
      $siteHandle: String!
    ) {
      responses(
        distinct_on: activity_slug
        where: {
          sharing: {
            _contains: "share-on-website"
          }
          approved_at: {
            _is_null: false
          }
        }
      ) {
        activity(
          site: [$siteHandle]
        ) {
          ...on activities_default_Entry {
            id
            slug
            title
            activityType {
              id
              slug
              title
            }
          }
        }
      }
    }
  `, {}, locale )

  const activities = activitiesResult?.data?.responses?.length
    ? activitiesResult.data.responses.map( r => r.activity[ 0 ] )
    : []

  const artforms = activities?.length
    ? activities.reduce( 
      ( collected, activity ) => [
        ...collected,
        ...activity.activityType.map( activityType => {
          if ( !collected.find( at => at.value === activityType.slug ) ) {
            return {
              title: activityType.title,
              value: activityType.slug,
            }
          }

          return null
        } ).filter( activityType => !!activityType ),
      ],
      [],
    )
    : []
  
  const countryCodesResult = useAnonymousQuery( gql`
    query ResponseCountryCodesQuery {
      responses(distinct_on: country) {
        country
      }
    }
  ` )

  const countryCodes = countryCodesResult?.data?.responses?.length
    ? countryCodesResult.data.responses.filter( r => !!r.country ).map( r => ( {
      title: r.country,
      value: r.country,
    } ) )
    : []
  
  const agesResult = useAnonymousQuery( gql`
    query ResponseAgesQuery {
      responses(distinct_on: age) {
        age
      }
    }
  ` )

  const ages = agesResult?.data?.responses?.length
    ? agesResult.data.responses.map( r => ( {
      title: r.age,
      value: r.age,
    } ) )
    : []

  return { 
    activities,
    countryCodes,
    ages,
    artforms,
  }
}
